import React from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import CowDetail from './CowDetail.tsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import RecordCollaborationSection from '../shared/RecordCollaborationSection.jsx';
import RecordSequenceNav from '../shared/RecordSequenceNav.tsx';
import {recordSeqNavOptions} from '../lib/recordSequence.js';
/* eslint-disable no-unused-vars -- shell primitives are used in JSX only */
import {
  RecordPageFrame,
  RecordPageLoading,
  RecordPageNotFound,
  RecordPageBody,
  RecordBackLink,
  RecordTitle,
} from '../shared/RecordPageShell.jsx';
/* eslint-enable no-unused-vars */
// eslint-disable-next-line no-unused-vars -- JSX-only use
import InlineNotice from '../shared/InlineNotice.jsx';
import RecordPageLoadError from '../shared/RecordPageLoadError.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {MUTATION} from '../app/data/routeMutationCatalog.js';
import {newCattleRequestId, runCattleOperation} from '../lib/cattleOperationsApi.js';
import type {CattleAnimalPatchVars, CattleCalvingCreateVars} from '../app/data/routeMutationCatalog.js';
import {useRouteMutation} from '../app/data/useRouteMutation.js';
import {CATTLE_ALL_HERD_KEYS, CATTLE_HERD_KEYS, cowTagSet} from '../lib/cattleHerdFilters.js';
import {softDeleteCattleAnimal} from '../lib/cattleDeleteApi.js';
import {transferCattleAnimal} from '../lib/animalTransferApi.js';
import {deleteCattleCalvingRecord} from '../lib/cattleCalvingApi.js';
import {canWriteLivestockRecords, canWriteLivestockTables} from '../lib/access.js';
import {reportSaving, reportSaved, reportSaveFailed} from '../lib/saveStatus.js';
import {formatRuminantAge, formatRuminantAgeAtDate} from './ruminantPresentation.js';
import Header from '../shared/Header.tsx';

const HERD_LABELS = {
  mommas: 'Mommas',
  backgrounders: 'Backgrounders',
  finishers: 'Finishers',
  bulls: 'Bulls',
  sold: 'Sold',
  deceased: 'Deceased',
  processed: 'Processed',
};

type CattleRecord = Record<string, any>;
type Notice = {kind?: 'error' | 'warning' | 'success' | 'info'; message?: string | null};
type CattleAnimalPageProps = {sb: any; fmt: (...args: any[]) => any; authState: any};

// The catalog rethrows PostgREST's `error.message` unchanged, so reading
// `.message` off the rejection reproduces the notice text the `{error}`
// envelope produced — including the "undefined" a message-less error would have
// rendered before. This is a conversion, not a rewording.
const writeErrorMessage = (err: unknown): string =>
  err && typeof err === 'object' ? String((err as {message?: unknown}).message) : String(err);

function age(birth: any) {
  return formatRuminantAge(birth, 'mo');
}

function ageAtDate(birth: any, endDate: any) {
  return formatRuminantAgeAtDate(birth, endDate, 'mo');
}

function processingBatchDate(batch: CattleRecord | null | undefined) {
  if (!batch) return '';
  return batch.actual_process_date || batch.planned_process_date || '';
}

function terminalAgeInfo(cow: CattleRecord | null, processingBatch: CattleRecord | null) {
  if (!cow) return {fieldLabel: 'Age', ageLabel: '—'};
  if (cow.herd === 'processed') {
    return {
      fieldLabel: 'Age at processing',
      ageLabel: ageAtDate(cow.birth_date, processingBatchDate(processingBatch)) || '—',
    };
  }
  if (cow.herd === 'sold') {
    return {
      fieldLabel: 'Age at sale',
      ageLabel: ageAtDate(cow.birth_date, cow.sale_date) || '—',
    };
  }
  if (cow.herd === 'deceased') {
    return {
      fieldLabel: 'Age at death',
      ageLabel: ageAtDate(cow.birth_date, cow.death_date) || '—',
    };
  }
  return {fieldLabel: 'Age', ageLabel: age(cow.birth_date) || '—'};
}

export default function CattleAnimalPage({sb, fmt, authState}: CattleAnimalPageProps) {
  const navigate = useNavigate();
  const location: any = useLocation();
  const cattleId = location.pathname.replace('/cattle/herds/', '');
  const routeState = (location.state || {}) as CattleRecord;
  const fromCowId = routeState.fromCowId || null;
  const fromCowTag = routeState.fromCowTag || null;
  // Originating list order (visible rows) handed through route state. Absent on
  // direct links, notifications, and cow-to-cow click-throughs.
  const recordSeq = location.state?.recordSeq || null;

  // B3.3 PILOT: the record page's seven reads come from the
  // cattle.animal.detail resource, KEYED ON cattleId. That keying is the fix
  // for a real race: the old loader effect had no cancellation, so navigating
  // A -> B let a late response for A overwrite B's state (last-write-wins) and
  // A's `finally` could end B's spinner early. A key change now aborts the
  // in-flight request and drops any settle belonging to the superseded key.
  const record = useRouteResource(
    RESOURCE.CATTLE_ANIMAL_DETAIL,
    React.useMemo(() => ({cattleId}), [cattleId]),
  );
  const recordData = (record.data || {}) as CattleRecord;
  const cow = recordData.cow || null;
  const cattle = recordData.cattle || [];
  const weighIns = recordData.weighIns || [];
  const processingBatches = recordData.processingBatches || [];
  const calvingRecs = recordData.calvingRecords || [];
  const breedOpts = recordData.breedOptions || [];
  const originOpts = recordData.originOptions || [];
  const loading = record.loading;
  const loadAll = record.refetch;
  // The record page's declared writes, handed the `sb` this surface was given
  // rather than the module singleton. The retired `await loadAll()` refreshed
  // this page only; the entries name all six resources that read a calving row
  // (including the breeding page and the forecast) and all twelve that read a
  // cattle row.
  const createCalving = useRouteMutation<CattleCalvingCreateVars, null>(MUTATION.CATTLE_CALVING_CREATE, {client: sb});
  const patchCattleAnimal = useRouteMutation<CattleAnimalPatchVars, null>(MUTATION.CATTLE_ANIMAL_PATCH, {client: sb});
  const [notice, setNotice] = React.useState<Notice | null>(null);
  // A missing animal is `null` DATA from maybeSingle(), not an error — the
  // resource preserves that distinction, so "this animal does not exist" and
  // "the page failed" stay different screens.
  const recordError = record.error as CattleRecord | null;
  const loadError: Notice | null = recordError
    ? {
        kind: 'error',
        message:
          'Could not load cattle record. Please refresh the page. (' +
          (recordError.message || String(recordError)) +
          ')',
      }
    : null;

  // loadAll and its cattleId effect are gone: the resource owns fetching,
  // cancellation and the key. `loadAll` above is its refetch.
  //
  // But the resource owns the DATA, not this surface's own UI state. The old
  // [cattleId] effect also cleared `notice`, and dropping it left a save/patch
  // error from cow A rendered over cow B after prev/next — `notice` is now
  // cleared only at the start of a mutation or by manual dismiss. `loadError`
  // is fine (derived from record.error, which the resource resets on a key
  // change); this is the one piece that was genuinely surface-local.
  React.useEffect(() => {
    setNotice(null);
  }, [cattleId]);

  React.useEffect(() => {
    if (!loading && location.hash) {
      const anchor = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 200);
    }
  }, [loading, location.hash]);

  async function patchCow(fields: CattleRecord) {
    if (!cow || !fields) return;
    setNotice(null);
    // Migration 245: a tag edit is the audited animal.retag operation — the
    // server composes old_tags (source 'manual', the rule preservePreviousTag
    // used to apply here); every other field is the audited animal.patch with
    // per-field compare-and-set, and its field.updated Activity row is written
    // in the same transaction.
    const saveKey = `cattle-animal:${cow.id}:${Object.keys(fields).sort().join(',')}`;
    if ('tag' in fields) {
      const saveAttempt = reportSaving(saveKey);
      try {
        await runCattleOperation(sb, {
          requestId: newCattleRequestId(),
          operation: 'animal.retag',
          target: {cattle_id: cow.id},
          expected: {tag: cow.tag ?? null},
          change: {tag: (fields.tag ?? null) as string | null, source: 'manual'},
        });
        reportSaved(saveKey, saveAttempt);
      } catch (err) {
        const msg = 'Save failed: ' + writeErrorMessage(err);
        setNotice({kind: 'error', message: msg});
        reportSaveFailed(saveKey, () => void patchCow(fields), saveAttempt);
      }
      return;
    }
    const expected: CattleRecord = {};
    for (const key of Object.keys(fields)) expected[key] = cow[key] ?? null;
    const saveAttempt = reportSaving(saveKey);
    try {
      await patchCattleAnimal.run({cattleId: cow.id, patch: fields, expected});
      reportSaved(saveKey, saveAttempt);
    } catch (err) {
      const msg = 'Save failed: ' + writeErrorMessage(err);
      setNotice({kind: 'error', message: msg});
      reportSaveFailed(saveKey, () => void patchCow(fields), saveAttempt);
      return;
    }
    // The trailing `loadAll()` is gone; the reason it replaced an optimistic
    // local patch is not, so it is recorded here rather than lost with it.
    //
    // That optimistic patch of two local mirrors carried the sharpest race on
    // this surface: after prev/next, a late response for cow A merged A's
    // fields into cow B's state, or into null while B loaded — producing a
    // fields-only pseudo-cow until B finished. Refetching does not merely FENCE
    // that race, it removes it: with no local mirror to merge into, a late
    // write has nowhere to land, and the resource's own key check already drops
    // settles for a superseded cattleId. `cattle.animal.patch` invalidates this
    // page's resource among the twelve that read a cattle row, so the refetch
    // still happens — it is now declared instead of remembered, and it reaches
    // the other eleven surfaces this call site never could.
  }

  async function transferCow(newHerd: string) {
    if (!cow) return;
    // No-op short-circuit; the RPC also no-ops, but this avoids a round trip.
    if (newHerd === cow.herd) return;
    if (newHerd === 'processed') {
      setNotice({
        kind: 'warning',
        message: 'Use Send to processor in WeighIns to record the cattle, weights, and processing batch together.',
      });
      return;
    }
    setNotice(null);
    const saveKey = `cattle-transfer:${cow.id}`;
    const saveAttempt = reportSaving(saveKey);
    try {
      // Transactional RPC: updates the row, writes the cattle_transfers audit
      // row, and logs a status.changed Activity event atomically. No more
      // "moved but audit failed" partial state.
      await transferCattleAnimal(sb, cow.id, newHerd, authState && authState.name ? authState.name : null);
      await loadAll();
      reportSaved(saveKey, saveAttempt);
    } catch (e) {
      const error = e as CattleRecord;
      const msg = 'Transfer failed: ' + (error.message || String(e));
      setNotice({kind: 'error', message: msg});
      reportSaveFailed(saveKey, () => void transferCow(newHerd), saveAttempt);
    }
  }

  async function addCalvingRecord(cowRecord: CattleRecord, formData: CattleRecord) {
    setNotice(null);
    if (!formData.calving_date) {
      setNotice({kind: 'error', message: 'Calving date required.'});
      return false;
    }
    if (formData.complications_flag && !(formData.complications_desc || '').trim()) {
      setNotice({kind: 'error', message: 'Complications description required when complications flag is set.'});
      return false;
    }
    const id = String(Date.now()) + Math.random().toString(36).slice(2, 6);
    const rec = {
      id,
      dam_tag: cowRecord.tag,
      calving_date: formData.calving_date,
      calf_tag: formData.calf_tag || null,
      sire_tag: formData.sire_tag || null,
      total_born: parseInt(formData.total_born) || 0,
      deaths: parseInt(formData.deaths) || 0,
      complications_flag: !!formData.complications_flag,
      complications_desc: formData.complications_desc || null,
      notes: formData.notes || null,
    };
    try {
      await createCalving.run({record: rec, damCattleId: cowRecord.id});
    } catch (err) {
      setNotice({kind: 'error', message: 'Save failed: ' + writeErrorMessage(err)});
      return false;
    }
    // The record.created Activity row (scoped to the dam, payload
    // record 'cattle.calving') is written by the calving.create operation in
    // the same transaction — migration 245.
    // The `await loadAll()` that stood here is gone. `createCalving.run` awaits
    // the declared invalidation set, which contains this page's resource AND
    // the five others that read a calving row; loadAll refreshed only this one.
    //
    // The reordering relative to the Activity emit above is deliberate and
    // safe: the refetch now happens BEFORE the emit rather than after, and none
    // of the six invalidated resources reads activity_events — the record
    // feed is its own surface. Nothing that was refreshed after the emit is
    // refreshed before it.
    return true;
  }

  async function deleteCalvingRecord(recId: string) {
    if (!window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete this calving record?', async () => {
      try {
        // Transactional RPC: deletes the row + logs a deletion Activity event
        // scoped to the dam, atomically.
        await deleteCattleCalvingRecord(sb, recId, authState && authState.name ? authState.name : null);
        await loadAll();
      } catch (e) {
        const error = e as CattleRecord;
        setNotice({kind: 'error', message: 'Delete failed: ' + (error.message || String(e))});
      }
    });
  }

  async function deleteCow() {
    if (!cow || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete(
      'Delete this cow record? Admin/backend recovery is available, but this record will be hidden from active cattle views.',
      async () => {
        try {
          await softDeleteCattleAnimal(sb, cow.id, cow.tag || cow.id);
          navigate('/cattle/herds');
        } catch (e) {
          const error = e as CattleRecord;
          setNotice({kind: 'error', message: 'Delete failed: ' + (error.message || String(e))});
        }
      },
    );
  }

  function navigateToCow(target: CattleRecord) {
    if (!target || !target.id) return;
    navigate('/cattle/herds/' + target.id, {
      state: {fromCowId: cow.id, fromCowTag: cow.tag || cow.id},
    });
  }

  // Prev/Next within the originating list sequence — carry the sequence
  // forward so the neighbor record keeps its controls.
  function navigateSeq(id: any) {
    navigate('/cattle/herds/' + id, recordSeqNavOptions(recordSeq));
  }

  if (loading) {
    return <RecordPageLoading Header={Header} />;
  }

  if (loadError) {
    return (
      <RecordPageLoadError
        backLabel="Back to Herds"
        onBack={() => navigate('/cattle/herds')}
        notice={loadError}
        onRetry={loadAll}
        data-cattle-animal-load-error="true"
      />
    );
  }

  if (!cow) {
    return (
      <RecordPageNotFound
        Header={Header}
        backLabel="Back to Herds"
        onBack={() => navigate('/cattle/herds')}
        message="Cattle record not found. It may have been deleted."
      />
    );
  }

  const cowWeighIns = weighIns.filter((w: CattleRecord) => {
    const tags = cowTagSet(cow);
    return tags.has(w.tag);
  });
  const processingBatch = processingBatches.find((batch: CattleRecord) => batch.id === cow.processing_batch_id) || null;
  const cowAge = terminalAgeInfo(cow, processingBatch);

  return (
    <RecordPageFrame Header={Header}>
      <RecordPageBody data-cattle-animal-page="1">
        <RecordBackLink label="Back to Herds" onBack={() => navigate('/cattle/herds')} />

        <RecordSequenceNav seq={recordSeq} currentId={cattleId} onNavigate={navigateSeq} />

        <RecordTitle>{cow.tag ? '#' + cow.tag : 'Untagged animal'}</RecordTitle>

        {notice && <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />}
        {cow.herd === 'processed' && !cow.processing_batch_id && (
          <InlineNotice
            notice={{
              kind: 'warning',
              message:
                'This animal is marked processed but has no processing batch. Its processing date and batch weights have not been recorded.',
            }}
          />
        )}

        <CowDetail
          key={cow.id}
          cow={cow}
          ageFieldLabel={cowAge.fieldLabel}
          ageLabel={cowAge.ageLabel}
          weighIns={cowWeighIns}
          calving={calvingRecs.filter((r: CattleRecord) => r.dam_tag === cow.tag)}
          comments={[]}
          calves={cattle.filter((x: CattleRecord) => x.dam_tag === cow.tag)}
          dam={cattle.find((x: CattleRecord) => x.tag === cow.dam_tag)}
          cattleList={cattle}
          fmt={fmt}
          HERDS={CATTLE_ALL_HERD_KEYS}
          HERD_LABELS={HERD_LABELS}
          onEdit={() => {}}
          onTransfer={(newHerd) => transferCow(newHerd)}
          transferLocked={!canWriteLivestockRecords(authState)}
          onDelete={authState?.role === 'admin' ? () => deleteCow() : undefined}
          onComment={null}
          onEditComment={null}
          onDeleteComment={null}
          hideComments={true}
          onAddCalving={canWriteLivestockTables(authState) ? (data) => addCalvingRecord(cow, data) : undefined}
          onDeleteCalving={canWriteLivestockRecords(authState) ? (id) => deleteCalvingRecord(id) : undefined}
          onNavigateToCow={(target) => navigateToCow(target)}
          onNavigateBack={() => navigate('/cattle/herds/' + fromCowId)}
          canNavigateBack={Boolean(fromCowId)}
          backToTag={fromCowTag}
          readOnly={!canWriteLivestockTables(authState)}
          onPatch={canWriteLivestockTables(authState) ? (fields) => patchCow(fields) : undefined}
          onClose={() => navigate('/cattle/herds')}
          originOpts={originOpts}
          breedOpts={breedOpts}
        />

        <RecordCollaborationSection
          sb={sb}
          authState={authState}
          entityType="cattle.animal"
          entityId={cow.id}
          entityLabel={cow.tag || cow.id}
        />
      </RecordPageBody>
    </RecordPageFrame>
  );
}
